import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import styles from './HFInputs.module.sass'
import HFTextInput from './HFTextInput'
import HFDropdown from './HFDropdown'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import AlertBox from 'components/AlertBox/AlertBox'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { useWatch, useFormContext } from 'react-hook-form'
import { Options } from 'models'
import { useFetchList } from 'store'
import { useEffect, useRef, useState } from 'react'
import { API_URL } from 'store/fetchUtils'
import { SuggestType } from '../../utils/enums'
import { useJustifications } from '../../hooks/useJustifications'
import JustificationField from '../JustificationField/JustificationField'

const JUSTIFICATION_CATEGORY_PHYSIOTHERAPY = 2
const JUSTIFICATION_CATEGORY_IMAGING = 1

function extractCode(input) {
	const regex = /^(\d{2}(?:\.\d{2}){3})/
	const match = input?.match(regex)
	return match ? match[1] : null
}

const getSymbol = (value, className) => {
	const iconProps = {
		style: { fill: className, marginRight: '5px' },
		width: value === 4 ? '16px' : '14px',
		height: value === 4 ? '16px' : '14px',
	}

	const icons = {
		0: 'fast-upward-one',
		1: 'fast-upward-two',
		2: 'fast-downward-one',
		3: 'fast-downward-two',
		4: 'equals',
	}

	const iconName = icons[value]
	if (!iconName) return null

	return <Icon name={iconName} {...iconProps} />
}

const parseProviderLine = (line) => {
	if (!line.includes(';')) return null

	const rowData = line.split(';')
	let prestador = ''
	let distancia = ''
	let valorData = ''
	let rapidez = ''
	let symbol = ''
	let className = ''

	rowData.forEach((data, index) => {
		switch (index) {
			case 0:
				prestador = data.replace(/['"()]/g, '').trim()
				break
			case 1:
				valorData = data ? `${Number(data).toFixed(2)} € |` : '- € |'
				break
			case 2:
				distancia = data ? `${data} km | ` : '- km | '
				break
			case 3:
				rapidez = data ? `${data} d ` : '- d '
				break
			case 4:
				if (data) {
					const percentagem = Number(data)
					className =
						percentagem <= -10
							? '#8B0000'
							: percentagem > -10 && percentagem <= 0
							? '#8B0000'
							: percentagem > 0 && percentagem <= 10
							? '#d3b610'
							: percentagem > 10 && percentagem <= 30
							? '#23a823'
							: '#23a823'

					const number =
						percentagem <= -10
							? 3
							: percentagem > -10 && percentagem <= 0
							? 2
							: percentagem > 0 && percentagem <= 10
							? 4
							: percentagem > 10 && percentagem <= 30
							? 0
							: 1
					symbol = getSymbol(number, className)
				} else {
					className = 'currentColor'
				}
				break
			default:
				break
		}
	})

	return { prestador, distancia, valorData, rapidez, symbol }
}

const HFMedicalActsProtocolRow = (props) => {
	const {
		name,
		index,
		processId,
		totalRequisitionsForMedicalAct,
		medicalActs,
		accidentType,
		postalCode,
		remove,
		onSuggestion,
		injuredBirthday,
		injuredType,
		bodyPart,
		requestingProviderId,
		appointmentDate,
		scheduleDate,
		insuranceId,
		blockCountDispatcher = () => {},
		onJustificationChanged = () => {},
		validateRef,
		showWarning,
		requisitionStateId,
	} = props

	const { i18n } = useGlobal()
	const [prestadorData, setPrestadorData] = useState('')
	const [isLoadingRobot, setIsLoadingRobot] = useState(false)
	const [selectedProvider, setSelectedProvider] = useState(null)
	const [pendingProvider, setPendingProvider] = useState(null)
	const [selectedUserJustification, setSelectedUserJustification] =
		useState(null)
	const [selectedUserJustificationId, setSelectedUserJustificationId] =
		useState(null)
	const prevActRef = useRef(null)
	const suggestionRef = useRef(null)

	const { register, setValue, getValues } = useFormContext()

	const medicalActId = useWatch({ name: `${name}.${index}.medicalActId` })
	const providerId = useWatch({ name: `${name}.${index}.serviceProviderId` })
	const sent = useWatch({ name: `${name}.${index}.sent` })

	const medicalActText = medicalActs?.find(
		(medicalAct) => medicalAct?.value === medicalActId
	)
	const medicalActNumber = medicalActText?.text?.match(
		/^\d{2}\.\d{2}\.\d{2}\.\d{2}/
	)?.[0]
	const typeMedicalActNumber = medicalActNumber?.split?.('.')[0] || ''

	const isImagingType = ['62', '64', '65'].includes(
		typeMedicalActNumber.toString().substring(0, 2)
	)
	const isPhysiotherapyType = ['90'].includes(
		typeMedicalActNumber.toString().substring(0, 2)
	)
	const justificationCategory = isImagingType
		? JUSTIFICATION_CATEGORY_IMAGING
		: JUSTIFICATION_CATEGORY_PHYSIOTHERAPY
	const { justifications } = useJustifications(justificationCategory)

	const handleJustificationChange = (justification) => {
		if (!justification) {
			setSelectedUserJustification(null)
			setSelectedUserJustificationId(null)
			setValue(`${name}.${index}.userJustification`, null)
			setValue(`${name}.${index}.userJustificationId`, null)
			onJustificationChanged(medicalActId, null, null)
			return
		}
		setSelectedUserJustification(justification.justificationName)
		setSelectedUserJustificationId(justification.justificationId)
		setValue(
			`${name}.${index}.userJustification`,
			justification.justificationName
		)
		setValue(
			`${name}.${index}.userJustificationId`,
			justification.justificationId
		)
		onJustificationChanged(
			medicalActId,
			justification.justificationName,
			justification.justificationId
		)
	}

	const handleJustificationCancel = () => {
		if (pendingProvider) {
			const {
				provider,
				lineIndex,
				name: fieldName,
				index: fieldIndex,
			} = pendingProvider
			setValue(`${fieldName}.${fieldIndex}.serviceProviderId`, null, {
				shouldValidate: true,
				shouldDirty: true,
			})
		}
		setSelectedProvider(null)
		setPendingProvider(null)
		setSelectedUserJustification(null)
		setSelectedUserJustificationId(null)
		setValue(`${name}.${index}.userJustification`, null)
		setValue(`${name}.${index}.userJustificationId`, null)
		onJustificationChanged(medicalActId, null, null)
	}

	const ownBlocked = pendingProvider !== null && !selectedUserJustification

	useEffect(() => {
		if (validateRef) {
			validateRef.current[medicalActId] = ownBlocked
		}
	}, [ownBlocked, medicalActId, validateRef])

	const commaIndex = prestadorData.lastIndexOf(';,')
	const mensagemFinal =
		commaIndex >= 0
			? prestadorData.substring(commaIndex + 2)
			: prestadorData.startsWith(',')
			? prestadorData.substring(1)
			: ''

	const sanitizeDate = (dateStr) => {
		if (!dateStr) return null
		const parts = dateStr.split('-')
		if (parts.length !== 3) return null
		const [year, month, day] = parts
		if (parseInt(month) > 12) return `${year}-${day}-${month}`
		return dateStr
	}

	useEffect(() => {
		const current = getValues(`${name}.${index}.sent`)
		if (typeof current === 'undefined') {
			setValue(`${name}.${index}.sent`, false, {
				shouldValidate: false,
				shouldDirty: false,
			})
		}
	}, [index, getValues, setValue, name])

	const handleRemove = () => {
		if (prevActRef.current && typeof onSuggestion === 'function') {
			onSuggestion(null, null, String(prevActRef.current))
		}
		remove(index)
	}

	const medicalActCode = extractCode(medicalActText?.text)

	const providerEntitiesUri =
		medicalActCode &&
		`/metadata/service_providers/medical_act/${medicalActCode}/${processId}`
	const { contents, state } = useFetchList(
		[providerEntitiesUri],
		[Options.fromSimpleObjectJSON]
	)
	const isLoading = state === 'loading'
	const [providerEntities = []] = contents || []
	const providerEntitiesOptions = isLoading
		? [{ value: '', text: i18n`Loading...` }]
		: [{ value: '', text: i18n`None` }, ...(providerEntities || [])]

	useEffect(() => {
		if (selectedProvider !== null && providerId) {
			const provider =
				suggestionRef.current?.response?.providerReferrals?.[
					selectedProvider
				]
			if (provider && providerId !== provider.providerId.toString()) {
				setSelectedProvider(null)
			}
		}
	}, [providerId, selectedProvider])

	// IMAGIOLOGIA
	useEffect(() => {
		if (
			!medicalActNumber ||
			!['62', '64', '65'].includes(
				typeMedicalActNumber.toString().substring(0, 2)
			) ||
			(requisitionStateId != null && requisitionStateId !== 3)
		) {
			return
		}

		const fetchImagingSuggestions = async () => {
			try {
				setIsLoadingRobot(true)
				const numSeguradora = insuranceId
				const extractedCode = extractCode(medicalActText?.text)

				const response = await fetch(
					`${API_URL}/api/suggest/imaging/suggestions`,
					{
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							branch: accidentType,
							insuranceId: Number(numSeguradora),
							codes: [medicalActId],
							postalCode7: postalCode,
							requestingProviderId: requestingProviderId || null,
							appointmentDate: appointmentDate
								? appointmentDate.split('T')[0] + 'T00:00:00'
								: null,
							scheduleDate: scheduleDate
								? scheduleDate.split('T')[0] + 'T00:00:00'
								: null,
						}),
					}
				)

				if (!response.ok) throw new Error('Network response was not ok')

				const jsonResponse = await response.json()

				suggestionRef.current = {
					response: jsonResponse,
					medicalActCode: extractedCode,
					suggestType: SuggestType.Imaging,
				}

				const displayText =
					jsonResponse.providerReferrals
						.map((p) => {
							const distanceKm = (p.distance / 1000).toFixed(2)
							const daysFormatted = p.averageTime.toFixed(2)
							const priceFormatted = p.price.toFixed(2)
							const percentagem = (
								((p.retailPrice - p.price) / p.price) *
								100
							).toFixed(2)
							return `${p.providerName};${priceFormatted};${distanceKm};${daysFormatted};${percentagem};`
						})
						.join('\\n') +
					',' +
					jsonResponse.description

				setPrestadorData(displayText)

				if (typeof onSuggestion === 'function' && medicalActId) {
					onSuggestion(
						String(medicalActId),
						{
							actId: medicalActId,
							medicalActCode: extractedCode,
							providerId,
							sent: sent === true,
							response: jsonResponse,
							suggestType: SuggestType.Imaging,
						},
						prevActRef.current ? String(prevActRef.current) : null
					)
				}
			} catch (error) {
				console.error('Erro ao carregar prestadores:', error)
				setPrestadorData('Erro ao carregar prestadores')
			} finally {
				setIsLoadingRobot(false)
			}
		}

		fetchImagingSuggestions()
	}, [
		medicalActNumber,
		requestingProviderId,
		appointmentDate,
		scheduleDate,
		requisitionStateId,
	])

	// FISIOTERAPIA
	useEffect(() => {
		if (
			!medicalActNumber ||
			!['90'].includes(typeMedicalActNumber.toString().substring(0, 2))
		) {
			return
		}

		const fetchPhysiotherapySuggestions = async () => {
			try {
				setIsLoadingRobot(true)
				const numSeguradora = insuranceId
				const extractedCode = extractCode(medicalActText?.text)

				const jsonResponse = await fetch(
					`${API_URL}/api/suggest/physiotherapy/suggestions`,
					{
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							branch: accidentType,
							insuranceId: Number(numSeguradora),
							medicalActId: medicalActId,
							postalCode7: postalCode,
							requestingProviderId: requestingProviderId || null,
							birthDate: sanitizeDate(injuredBirthday),
							injuryTypeId: injuredType,
							injuryBodyPartId: bodyPart,
						}),
					}
				).then((r) => r.json())
				suggestionRef.current = {
					response: jsonResponse,
					medicalActCode: extractedCode,
					suggestType: SuggestType.Physiotherapy,
				}

				const displayText =
					jsonResponse.providerReferrals
						.map((p) => {
							const distanceKm = (p.distance / 1000).toFixed(2)
							const daysFormatted = p.averageTime.toFixed(2)
							const priceFormatted = p.price.toFixed(2)
							const percentagem = (
								((p.retailPrice - p.price) / p.price) *
								100
							).toFixed(2)
							return `${p.providerName};${priceFormatted};${distanceKm};${daysFormatted};${percentagem};`
						})
						.join('\\n') +
					',' +
					jsonResponse.description

				setPrestadorData(displayText)

				if (typeof onSuggestion === 'function' && medicalActId) {
					onSuggestion(
						String(medicalActId),
						{
							actId: medicalActId,
							medicalActCode: extractedCode,
							providerId,
							sent: sent === true,
							response: jsonResponse,
							suggestType: SuggestType.Physiotherapy,
						},
						prevActRef.current ? String(prevActRef.current) : null
					)
				}
			} catch (error) {
				console.error('Error fetching suggestion:', error)
				setPrestadorData('Erro ao carregar prestadores')
			} finally {
				setIsLoadingRobot(false)
			}
		}
		fetchPhysiotherapySuggestions()
	}, [medicalActNumber, typeMedicalActNumber, requestingProviderId])

	useEffect(() => {
		if (!medicalActId || !providerId) return
		if (!suggestionRef.current) return

		if (typeof onSuggestion === 'function') {
			onSuggestion(
				String(medicalActId),
				{
					actId: medicalActId,
					medicalActCode: suggestionRef.current.medicalActCode,
					providerId,
					sent: sent === true,
					response: suggestionRef.current.response,
					suggestType: suggestionRef.current.suggestType,
					userJustification: selectedUserJustification,
					userJustificationId: selectedUserJustificationId,
				},
				null
			)
		}
	}, [providerId, selectedUserJustification])

	const shouldShowSuggestions =
		medicalActNumber &&
		typeMedicalActNumber &&
		(isImagingType || isPhysiotherapyType)

	return (
		<VStack>
			<HStack
				wrap='nowrap'
				gap='xsmall'
				alignItems='flex-start'
				style={{ marginTop: '30px' }}
				className={styles['process-protocol-field']}
			>
				<div style={{ flex: 1 }}>
					<HStack gap='small' columns={['75px', 'calc(100% - 75px)']}>
						<HStack className={styles['quantity-container']}>
							<small>({totalRequisitionsForMedicalAct})</small>
							<HFTextInput
								name={`${name}.${index}.quantity`}
								type='number'
								placeholder=''
								displaySuffixIcon={false}
								inputMode='numeric'
								multiline={false}
								noResize
								ignoreCustomValidity={false}
							/>
						</HStack>
						<div>
							<HFDropdown
								name={`${name}.${index}.medicalActId`}
								options={medicalActs || []}
								searchable={true}
								noData={i18n`No results`}
							/>
						</div>
					</HStack>
					<small>
						<strong>{i18n`Provider`}</strong>
					</small>
					<HFDropdown
						name={`${name}.${index}.serviceProviderId`}
						options={providerEntitiesOptions || []}
						searchable={true}
						noData={i18n`No results`}
						onChange={(e) => {
							const selectedProviderId = e?.target?.value
							if (
								!selectedProviderId ||
								!suggestionRef.current?.response
									?.providerReferrals?.length
							) {
								setValue(
									`${name}.${index}.serviceProviderId`,
									selectedProviderId,
									{
										shouldValidate: true,
										shouldDirty: true,
									}
								)
								setSelectedProvider(null)
								return
							}
							const parsedId = parseInt(selectedProviderId, 10)
							const firstProvider =
								suggestionRef.current.response
									.providerReferrals[0]
							const selectedProvider =
								suggestionRef.current.response.providerReferrals.find(
									(p) => Number(p.providerId) === parsedId
								)
							if (!selectedProvider) {
								setValue(
									`${name}.${index}.serviceProviderId`,
									selectedProviderId,
									{
										shouldValidate: true,
										shouldDirty: true,
									}
								)
								setSelectedProvider(null)
								setPendingProvider({
									provider: {
										providerId: parsedId,
										providerName: 'Manual Selection',
									},
									lineIndex: -1,
									name,
									index,
								})
								return
							}
							const isFirstProvider = firstProvider
								? Number(firstProvider.providerId) ===
								  Number(selectedProvider.providerId)
								: false
							if (isFirstProvider) {
								setValue(
									`${name}.${index}.serviceProviderId`,
									selectedProviderId,
									{
										shouldValidate: true,
										shouldDirty: true,
									}
								)
								const lineIndex =
									suggestionRef.current.response.providerReferrals.findIndex(
										(p) => Number(p.providerId) === parsedId
									)
								setSelectedProvider(lineIndex)
								setPendingProvider(null)
								setSelectedUserJustification(null)
								setSelectedUserJustificationId(null)
								setValue(
									`${name}.${index}.userJustification`,
									null
								)
								setValue(
									`${name}.${index}.userJustificationId`,
									null
								)
								onJustificationChanged(medicalActId, null, null)
							} else {
								const lineIndex =
									suggestionRef.current.response.providerReferrals.findIndex(
										(p) => Number(p.providerId) === parsedId
									)
								setValue(
									`${name}.${index}.serviceProviderId`,
									selectedProviderId,
									{
										shouldValidate: true,
										shouldDirty: true,
									}
								)
								setSelectedProvider(lineIndex)
								setPendingProvider({
									provider: selectedProvider,
									lineIndex,
									name,
									index,
								})
							}
						}}
					/>
					<input
						type='hidden'
						{...register(`${name}.${index}.sent`)}
					/>
					<input
						type='hidden'
						{...register(`${name}.${index}.userJustification`)}
					/>
					<input
						type='hidden'
						{...register(`${name}.${index}.userJustificationId`)}
					/>
				</div>
				<Icon
					className={styles['remove-button']}
					onClick={(e) => {
						e.preventDefault()
						handleRemove()
					}}
					name='minus'
					height='12px'
					width='12px'
				/>
			</HStack>

			{/* IMAGIOLOGIA */}
			{isImagingType && shouldShowSuggestions && (
				<>
					<fieldset>
						<VStack style={{ marginTop: '10px' }}>
							<HStack>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 24 24'
									fill='currentColor'
									width='24'
									height='24'
									style={{ fill: '#587587' }}
								>
									<path d='M12 2a1 1 0 011 1v1h4a2 2 0 012 2v3h1a1 1 0 110 2h-1v7a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-7H2a1 1 0 110-2h1V6a2 2 0 012-2h4V3a1 1 0 011-1zm-4 7a1 1 0 100 2 1 1 0 000-2zm8 0a1 1 0 100 2 1 1 0 000-2zm-8 4h8v5H8v-5z' />
								</svg>
								<h4 className={styles['noMargin']}>
									{i18n`Trust AI Tittle`}
								</h4>
							</HStack>
							<h4>{i18n`Trust AI Suggestion`}</h4>
						</VStack>

						{isLoadingRobot ? (
							<p>Loading...</p>
						) : (
							<>
								<h6 className={styles['noMargin']}>
									{i18n`Trust AI Sections`}
								</h6>
								{prestadorData &&
								prestadorData !==
									'Erro ao carregar prestadores' ? (
									prestadorData
										.split('\\n')
										.map((line, lineIndex) => {
											const parsed =
												parseProviderLine(line)
											if (!parsed) return null

											const {
												prestador,
												distancia,
												valorData,
												rapidez,
												symbol,
											} = parsed

											return (
												<div
													key={lineIndex}
													style={{
														paddingLeft: '10px',
														borderLeft: `3px solid ${
															selectedProvider ===
															lineIndex
																? '#4948ac'
																: '#4948ac33'
														}`,
														marginBottom: '10px',
														display: 'flex',
														justifyContent:
															'space-between',
														alignItems: 'center',
													}}
												>
													<div style={{ flex: 1 }}>
														<p
															style={{
																margin: 0,
															}}
														>
															{prestador}
														</p>
														<small
															style={{
																display: 'flex',
																alignItems:
																	'center',
															}}
														>
															{symbol}
															<span
																style={{
																	color: 'inherit',
																}}
															>
																{valorData}{' '}
																{distancia}{' '}
																{rapidez}
															</span>
														</small>
													</div>
													<button
														type='button'
														onClick={() => {
															const providers =
																suggestionRef
																	.current
																	?.response
																	?.providerReferrals
															const provider =
																providers?.[
																	lineIndex
																]
															if (!provider)
																return
															if (
																selectedProvider ===
																lineIndex
															) {
																setSelectedProvider(
																	null
																)
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	null
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																setValue(
																	`${name}.${index}.userJustification`,
																	null
																)
																setValue(
																	`${name}.${index}.userJustificationId`,
																	null
																)
																onJustificationChanged(
																	medicalActId,
																	null,
																	null
																)
																return
															}
															const firstProvider =
																providers?.[0]
															const isFirstProvider =
																firstProvider
																	? Number(
																			firstProvider.providerId
																	  ) ===
																	  Number(
																			provider.providerId
																	  )
																	: false
															if (
																isFirstProvider
															) {
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	provider.providerId.toString(),
																	{
																		shouldValidate: true,
																		shouldDirty: true,
																	}
																)
																setSelectedProvider(
																	lineIndex
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																setValue(
																	`${name}.${index}.userJustification`,
																	null
																)
																setValue(
																	`${name}.${index}.userJustificationId`,
																	null
																)
																onJustificationChanged(
																	medicalActId,
																	null,
																	null
																)
															} else {
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	provider.providerId.toString(),
																	{
																		shouldValidate: true,
																		shouldDirty: true,
																	}
																)
																setSelectedProvider(
																	lineIndex
																)
																setPendingProvider(
																	{
																		provider,
																		lineIndex,
																		name,
																		index,
																	}
																)
															}
														}}
														style={{
															backgroundColor:
																selectedProvider ===
																lineIndex
																	? '#4caf50'
																	: '#f5f5f5',
															border:
																selectedProvider ===
																lineIndex
																	? '1px solid #4caf50'
																	: '1px solid #e0e0e0',
															borderRadius: '4px',
															cursor: 'pointer',
															fontSize: '10px',
															fontWeight: '500',
															padding: '4px 8px',
															whiteSpace:
																'nowrap',
															transition:
																'all 0.15s ease',
														}}
														onMouseEnter={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#e8e8e8'
																e.currentTarget.style.borderColor =
																	'#ccc'
															}
														}}
														onMouseLeave={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#f5f5f5'
																e.currentTarget.style.borderColor =
																	'#e0e0e0'
															}
														}}
													>
														<span
															style={{
																color:
																	selectedProvider ===
																	lineIndex
																		? '#ffffff'
																		: '#555',
															}}
														>
															✓
														</span>
													</button>
												</div>
											)
										})
								) : (
									<p>
										{prestadorData ||
											'Erro ao tentar carregar os prestadores sugeridos'}
									</p>
								)}
								{mensagemFinal && (
									<p
										style={{
											marginTop: '10px',
											color: 'black',
											fontSize: '11px',
										}}
									>
										{mensagemFinal
											.replace(/['"()]/g, '')
											.trim()}
									</p>
								)}
							</>
						)}
					</fieldset>

					{pendingProvider && (
						<>
							{showWarning && !selectedUserJustification && (
								<AlertBox
									type='warning'
									items={[
										{
											title: i18n`Warning`,
											text: i18n`Justification is mandatory to proceed`,
										},
									]}
								/>
							)}
							<JustificationField
								justifications={justifications}
								providerName={
									pendingProvider.provider?.providerName
								}
								onJustificationChange={
									handleJustificationChange
								}
								onCancel={handleJustificationCancel}
							/>
						</>
					)}

					{/* Separador entre protocolos */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
							margin: '14px 0',
						}}
					>
						<div
							style={{
								flex: 1,
								height: '1px',
								background: '#4948ac33',
							}}
						/>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
								background: '#4948ac12',
								border: '1px solid #4948ac44',
								borderRadius: '20px',
								padding: '3px 12px',
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#4948ac',
									fontWeight: '500',
								}}
							>
								{i18n`Next protocol`}
							</span>
						</div>
						<div
							style={{
								flex: 1,
								height: '1px',
								background: '#4948ac33',
							}}
						/>
					</div>
				</>
			)}

			{/* FISIOTERAPIA */}
			{isPhysiotherapyType && shouldShowSuggestions && (
				<>
					<fieldset>
						<VStack
							style={{ marginTop: '0px', marginBottom: '10px' }}
						>
							<HStack>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 24 24'
									fill='currentColor'
									width='24'
									height='24'
									style={{ fill: '#587587' }}
								>
									<path d='M12 2a1 1 0 011 1v1h4a2 2 0 012 2v3h1a1 1 0 110 2h-1v7a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-7H2a1 1 0 110-2h1V6a2 2 0 012-2h4V3a1 1 0 011-1zm-4 7a1 1 0 100 2 1 1 0 000-2zm8 0a1 1 0 100 2 1 1 0 000-2zm-8 4h8v5H8v-5z' />
								</svg>
								<h4
									className={styles['noMargin']}
								>{i18n`Trust AI Tittle`}</h4>
							</HStack>
							<h4>{i18n`Trust AI Suggestion`}</h4>
						</VStack>

						{isLoadingRobot ? (
							<p>{i18n`loading suggestion`}</p>
						) : (
							<>
								<h6
									className={styles['noMargin']}
								>{i18n`Trust AI Sections`}</h6>
								{prestadorData &&
								prestadorData !==
									'Erro ao carregar prestadores' ? (
									prestadorData
										.split('\\n')
										.map((line, lineIndex) => {
											const parsed =
												parseProviderLine(line)
											if (!parsed) return null
											const {
												prestador,
												distancia,
												valorData,
												rapidez,
												symbol,
											} = parsed

											return (
												<div
													key={lineIndex}
													style={{
														paddingLeft: '10px',
														borderLeft: `3px solid ${
															selectedProvider ===
															lineIndex
																? '#4948ac'
																: '#4948ac33'
														}`,
														marginBottom: '10px',
														display: 'flex',
														justifyContent:
															'space-between',
														alignItems: 'center',
													}}
												>
													<div style={{ flex: 1 }}>
														<p
															style={{
																margin: 0,
															}}
														>
															{prestador}
														</p>
														<small
															style={{
																display: 'flex',
																alignItems:
																	'center',
															}}
														>
															{symbol}
															<span
																style={{
																	color: 'inherit',
																}}
															>
																{valorData}{' '}
																{distancia}{' '}
																{rapidez}
															</span>
														</small>
													</div>
													<button
														type='button'
														onClick={() => {
															const providers =
																suggestionRef
																	.current
																	?.response
																	?.providerReferrals
															const provider =
																providers?.[
																	lineIndex
																]
															if (!provider)
																return
															if (
																selectedProvider ===
																lineIndex
															) {
																setSelectedProvider(
																	null
																)
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	null
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																setValue(
																	`${name}.${index}.userJustification`,
																	null
																)
																setValue(
																	`${name}.${index}.userJustificationId`,
																	null
																)
																onJustificationChanged(
																	medicalActId,
																	null,
																	null
																)
																return
															}
															const firstProvider =
																providers?.[0]
															const isFirstProvider =
																firstProvider
																	? Number(
																			firstProvider.providerId
																	  ) ===
																	  Number(
																			provider.providerId
																	  )
																	: false
															if (
																isFirstProvider
															) {
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	provider.providerId.toString(),
																	{
																		shouldValidate: true,
																		shouldDirty: true,
																	}
																)
																setSelectedProvider(
																	lineIndex
																)
																setPendingProvider(
																	null
																)
																setSelectedUserJustification(
																	null
																)
																setSelectedUserJustificationId(
																	null
																)
																setValue(
																	`${name}.${index}.userJustification`,
																	null
																)
																setValue(
																	`${name}.${index}.userJustificationId`,
																	null
																)
																onJustificationChanged(
																	medicalActId,
																	null,
																	null
																)
															} else {
																setValue(
																	`${name}.${index}.serviceProviderId`,
																	provider.providerId.toString(),
																	{
																		shouldValidate: true,
																		shouldDirty: true,
																	}
																)
																setSelectedProvider(
																	lineIndex
																)
																setPendingProvider(
																	{
																		provider,
																		lineIndex,
																		name,
																		index,
																	}
																)
															}
														}}
														style={{
															backgroundColor:
																selectedProvider ===
																lineIndex
																	? '#4caf50'
																	: '#f5f5f5',
															border:
																selectedProvider ===
																lineIndex
																	? '1px solid #4caf50'
																	: '1px solid #e0e0e0',
															borderRadius: '4px',
															cursor: 'pointer',
															fontSize: '10px',
															fontWeight: '500',
															padding: '4px 8px',
															whiteSpace:
																'nowrap',
															transition:
																'all 0.15s ease',
														}}
														onMouseEnter={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#e8e8e8'
																e.currentTarget.style.borderColor =
																	'#ccc'
															}
														}}
														onMouseLeave={(e) => {
															if (
																selectedProvider !==
																lineIndex
															) {
																e.currentTarget.style.backgroundColor =
																	'#f5f5f5'
																e.currentTarget.style.borderColor =
																	'#e0e0e0'
															}
														}}
													>
														<span
															style={{
																color:
																	selectedProvider ===
																	lineIndex
																		? '#ffffff'
																		: '#555',
															}}
														>
															✓
														</span>
													</button>
												</div>
											)
										})
								) : (
									<p>
										{prestadorData ||
											'Erro ao tentar carregar os prestadores sugeridos'}
									</p>
								)}
								{mensagemFinal && (
									<p
										style={{
											marginTop: '10px',
											color: 'black',
											fontSize: '11px',
										}}
									>
										{mensagemFinal
											.replace(/['"()]/g, '')
											.trim()}
									</p>
								)}
							</>
						)}
					</fieldset>

					{pendingProvider && (
						<>
							{showWarning && !selectedUserJustification && (
								<AlertBox
									type='warning'
									items={[
										{
											title: i18n`Warning`,
											text: i18n`Justification is mandatory to proceed`,
										},
									]}
								/>
							)}
							<JustificationField
								justifications={justifications}
								providerName={
									pendingProvider.provider?.providerName
								}
								onJustificationChange={
									handleJustificationChange
								}
								onCancel={handleJustificationCancel}
							/>
						</>
					)}

					{/* Separador entre protocolos */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
							margin: '14px 0',
						}}
					>
						<div
							style={{
								flex: 1,
								height: '1px',
								background: '#4948ac33',
							}}
						/>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
								background: '#4948ac12',
								border: '1px solid #4948ac44',
								borderRadius: '20px',
								padding: '3px 12px',
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#4948ac',
									fontWeight: '500',
								}}
							>
								{i18n`Next protocol`}
							</span>
						</div>
						<div
							style={{
								flex: 1,
								height: '1px',
								background: '#4948ac33',
							}}
						/>
					</div>
				</>
			)}
		</VStack>
	)
}

export default HFMedicalActsProtocolRow
